import React from 'react';
import PropTypes from 'prop-types';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
// Shared with the block this same record is rendered into for the LLM, so the
// screen and the prompt can never disagree about what the patient's record says.
import { decapitalize, sexLabel } from 'utils/patientContext';
import {
  allergenName,
  hasItems,
  joinList,
  medicationName,
  norm,
  sameNames,
} from './recordSummaryHelpers';

/*
 * Extracted from SharedRecordSummary so the two places a check-in is read can
 * share one renderer: the clinician who redeemed the patient's 7-digit code, and
 * the clinic's own inbox. They see the same intake and must not describe it
 * differently — a "no" that appears in one and not the other is a clinical
 * disagreement produced by a copy-paste.
 *
 * The chart-echo suppression below is the reason `record` and `allowedSections`
 * are parameters rather than assumptions. The inbox has no chart to compare
 * against (a facility cannot read a patient's user document), so it passes
 * neither and every line the patient gave is printed. The code path passes both
 * and drops the four that would be printed twice.
 */

/**
 * The VisitStart pre-visit check-in the patient completed in the waiting room.
 *
 * FIRST ON THE PAGE, above the chart. This is why the patient is here today; the
 * record is the background they arrived with. It is also the newest thing on the
 * screen, and a clinician opening this mid-consultation is looking for today.
 *
 * Patient-reported, so it is labelled as such — the same treatment CareStart's
 * block gets, and the same distinction the LLM prompt draws between the
 * authoritative chart and the patient's own account. What it shows is wider than
 * what goes to the model: the ID number and the referral letter are here because
 * the front desk needs them to register someone, and excluded from the prompt
 * because identity tells a model nothing clinical.
 *
 * WHAT IT LEAVES OUT, and the one rule that governs it. For a signed-in patient
 * the check-in writes confirmed medications and allergies STRAIGHT TO THE CHART,
 * so when they also share their record the two lists are the same list and
 * printing both just doubles the reading. Same for date of birth and sex.
 *
 * So those four are dropped here — but only where the record above actually
 * agrees with them. A value that DIFFERS is the most interesting thing on the
 * page: it is either a chart write that did not land or a patient correcting
 * their own record, and a mismatched date of birth is a patient-identity
 * question. Blind suppression by section would hide exactly those. The block
 * says what it dropped, because a check-in showing no medications must not read
 * as a patient who was never asked.
 *
 * Each screening row carries the question it asked, so this needs no element
 * catalog and cannot drift out of step with one.
 */
const VisitStartIntakeSummary = ({
  intake,
  record = {},
  allowedSections = [],
  fallbackDob = null,
}) => {
  if (!intake) return null;

  const answers = intake.elementAnswers || {};
  const identity = answers.identity || {};
  const complaint = (answers['chief-complaint'] || {}).confirmedSummary;
  const meds = (answers['medication-list'] || {}).medications;
  const takingMeds = (answers['medication-list'] || {}).takingMedications;
  const allergies = (answers.allergies || {}).allergies;
  const referral = answers['referral-verification'] || {};

  // Shared AND matching. Either half missing means the line stays.
  const shows = (id) => allowedSections.includes(id);
  const demographics = record.demographics || {};
  const recordDob = demographics.dateOfBirth || fallbackDob;

  const echoedDob = Boolean(
    shows('demographics') &&
      identity.dateOfBirth &&
      recordDob &&
      norm(recordDob) === norm(identity.dateOfBirth),
  );
  const echoedSex = Boolean(
    shows('demographics') &&
      identity.sex &&
      demographics.sex &&
      norm(demographics.sex) === norm(identity.sex),
  );
  const echoedMeds = Boolean(
    shows('medications') &&
      hasItems(meds) &&
      sameNames(meds.map(medicationName), (record.medications || []).map(medicationName)),
  );
  const echoedAllergies = Boolean(
    shows('allergies') &&
      hasItems(allergies) &&
      sameNames(allergies.map(allergenName), (record.allergies || []).map(allergenName)),
  );

  const echoed = [
    echoedDob && 'date of birth',
    echoedSex && 'sex',
    echoedMeds && 'medications',
    echoedAllergies && 'allergies',
  ].filter(Boolean);

  // Only the yeses, from every screen that saves {answers:[...]}. A wall of "no" is
  // not what a clinician is scanning this for.
  const flagged = [];
  Object.values(answers).forEach((answer) => {
    if (!answer || !Array.isArray(answer.answers)) return;
    answer.answers.forEach((row) => {
      if (row && row.value === 'yes' && row.question) flagged.push(row.question);
    });
  });

  const unanswered = (intake.clarifyingQuestions || [])
    .map((q) => q && q.question)
    .filter(Boolean);

  const line = (label, value) =>
    value ? (
      <Typography variant="body2">
        <strong>{label}:</strong> {value}
      </Typography>
    ) : null;

  return (
    <Alert severity="info" icon={false}>
      <AlertTitle sx={{ fontWeight: 700 }}>Patient's pre-visit check-in</AlertTitle>
      {/* The second sentence is what stops a dropped section reading as a
          question nobody asked. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Answered by the patient before this visit. Patient-reported, and not yet
        verified at the desk.
        {echoed.length
          ? ` Their ${joinList(echoed)} matched the record below and is not repeated here.`
          : ''}
      </Typography>

      {line(
        identity.idType === 'huduma-namba' ? 'Huduma Namba' : 'National ID',
        identity.idNumber,
      )}
      {echoedDob ? null : line('Date of birth', identity.dateOfBirth)}
      {echoedSex ? null : line('Sex', identity.sex ? sexLabel(identity.sex) : null)}

      {complaint ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            Reason for the visit, in their own words
          </Typography>
          <Typography variant="body2">{complaint}</Typography>
        </Box>
      ) : null}

      {hasItems(meds) && !echoedMeds ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            Medications they listed
          </Typography>
          {meds.map((m, i) => (
            <Typography variant="body2" key={`${m?.name || 'med'}-${i}`}>
              {[m?.name, m?.dosage, m?.frequency].filter(Boolean).join(', ')}
            </Typography>
          ))}
        </Box>
      ) : takingMeds === false ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          They said they take no regular medications.
        </Typography>
      ) : null}

      {hasItems(allergies) && !echoedAllergies ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            Allergies they listed
          </Typography>
          {allergies.map((a, i) => (
            <Typography variant="body2" key={`${a?.allergen || 'allergy'}-${i}`}>
              {[a?.allergen, a?.reaction && decapitalize(a.reaction)]
                .filter(Boolean)
                .join(' - ')}
            </Typography>
          ))}
        </Box>
      ) : null}

      {flagged.length ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            Answered yes
          </Typography>
          {flagged.map((q) => (
            <Typography variant="body2" key={q}>
              {q}
            </Typography>
          ))}
        </Box>
      ) : null}

      {referral.hasReferral ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Referral letter</strong> from{' '}
          {referral.facilityName || 'a facility they did not name'}
          {referral.referralDate ? `, dated ${referral.referralDate}` : ''}.
        </Typography>
      ) : null}

      {unanswered.length ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            Preferred not to answer, or was unsure
          </Typography>
          {unanswered.map((q) => (
            <Typography variant="body2" key={q}>
              {q}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Alert>
  );
};

VisitStartIntakeSummary.propTypes = {
  /** `visitStartIntake` from redeemHealthRecordShareToken, or a session document. */
  intake: PropTypes.object,
  /** The chart shown alongside it, for echo suppression. Omit when there is none. */
  record: PropTypes.object,
  /** Which record sections the patient shared. Omit when there is no record. */
  allowedSections: PropTypes.arrayOf(PropTypes.string),
  /** Date of birth from the token payload, when the record itself lacks one. */
  fallbackDob: PropTypes.string,
};

export default VisitStartIntakeSummary;
